import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, FileText, Car, Truck, GaugeCircle, Map, Star, Zap, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { FaRupeeSign, FaClock, FaChartLine, FaPercentage, FaWallet, FaBalanceScale, FaCheckCircle, FaExchangeAlt } from "react-icons/fa";
import { BiCar } from "react-icons/bi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const MercedesBenzCarLoan = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "What is the minimum interest rate for a Mercedes-Benz car loan?",
      answer: "Interest rates generally start at 8.5% per annum.",
    },
    {
      question: "Can I get 100% financing for a Mercedes-Benz vehicle?",
      answer: "Typically, lenders finance up to 90% of the on-road price.",
    },
    {
      question: "Do Mercedes-Benz Financial Services offer better terms than banks?",
      answer: "They offer customized plans but it's ideal to compare options through TheFinMaster.",
    },
    {
      question: "Is a luxury car loan different from a regular car loan?",
      answer: "Yes, they involve higher loan amounts, stricter credit checks, and luxury-specific policies.",
    },
    {
      question: "Are used Mercedes-Benz cars eligible for financing?",
      answer: "Yes, certified pre-owned Mercedes-Benz cars can be financed.",
    },
    {
      question: "What credit score do I need to qualify?",
      answer: "A credit score of 750+ is preferred for luxury vehicle financing.",
    },
    {
      question: "Can I include insurance and registration costs in the loan?",
      answer: "Yes, many lenders bundle insurance and registration into the loan amount.",
    },
    {
      question: "Are business owners eligible for Mercedes-Benz car loans?",
      answer: "Yes, with valid financial documents and proof of business continuity.",
    },
    {
      question: "Is a co-applicant required?",
      answer: "Not mandatory, but having a co-applicant improves approval chances.",
    },
    {
      question: "How long does it take to get loan approval?",
      answer: "Typically within 48 to 96 hours post document verification.",
    },
    {
      question: "Can I apply online for a Mercedes-Benz car loan?",
      answer: "Yes, you can apply directly via TheFinMaster for digital approvals.",
    },
    {
      question: "Is prepayment or foreclosure allowed?",
      answer: "Yes, but check if your lender charges a penalty.",
    },
    {
      question: "Do women buyers get special offers?",
      answer: "Select banks may offer preferential interest rates to female borrowers.",
    },
    {
      question: "Can NRIs apply for a loan to buy a Mercedes-Benz in India?",
      answer: "Yes, subject to eligibility and presence of an Indian co-applicant.",
    },
    {
      question: "What EMI plans are available?",
      answer: "Step-up, balloon, and EMI holiday options are available from select banks.",
    },
    {
      question: "Is hypothecation mandatory for the loan?",
      answer: "Yes, until the loan is fully repaid.",
    },
    {
      question: "Are loans available for AMG performance models?",
      answer: "Yes, including higher ticket-size approvals and flexible plans.",
    },
    {
      question: "What is the minimum income requirement?",
      answer: "Typically, ₹1.2–₹1.5 lakh monthly income is recommended for luxury loans.",
    },
    {
      question: "Can I refinance my existing luxury car loan?",
      answer: "Yes, balance transfer options are available with better terms.",
    },
    {
      question: "Do government employees get any benefits?",
      answer: "Some public sector banks offer lower interest rates to government employees.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows EMI Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Loans' },
  ];

  const benefits = [
    {
      title: "Mercedes-Benz A-Class Limousine",
      desc: "Entry-level luxury with modern features and styling.",
      icon: <Car className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "Mercedes-Benz C-Class",
      desc: "A perfect blend of luxury, comfort, and performance.",
      icon: <Truck className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "Mercedes-Benz GLA",
      desc: "Compact luxury SUV with premium interiors and advanced tech.",
      icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
    {
      title: "Mercedes-Benz E-Class",
      desc: "Executive sedan known for refinement and status.",
      icon: <Map className="w-7 h-7 text-green-500" />,
      gradient: "from-green-50 to-green-100",
    },
    {
      title: "Mercedes-Benz GLE",
      desc: "Spacious, high-performance SUV designed for luxury families.",
      icon: <Star className="w-7 h-7 text-yellow-500" />,
      gradient: "from-yellow-50 to-yellow-100",
    },
    {
      title: "Mercedes-Benz GLS",
      desc: "Flagship SUV with unmatched features and road presence.",
      icon: <Zap className="w-7 h-7 text-purple-500" />,
      gradient: "from-purple-50 to-purple-100",
    },
    {
      title: "Mercedes-AMG Range",
      desc: "Performance-focused lineup for driving enthusiasts.",
      icon: <Shield className="w-7 h-7 text-blue-500" />,
      gradient: "from-blue-50 to-blue-100",
    },
  ];

  const highlights = [
    { icon: <FaRupeeSign className="text-3xl text-blue-700 mb-4" />, feature: "Loan Amount", details: "₹5 Lakh to ₹2 Crore+" },
    { icon: <FaClock className="text-3xl text-blue-700 mb-4" />, feature: "Loan Tenure", details: "1 to 7 years" },
    { icon: <FaChartLine className="text-3xl text-blue-700 mb-4" />, feature: "Interest Rate Range", details: "8.5% to 12.5% p.a." },
    { icon: <FaPercentage className="text-3xl text-blue-700 mb-4" />, feature: "Processing Fees", details: "0.25% to 1.5% of loan amount" },
    { icon: <FaWallet className="text-3xl text-blue-700 mb-4" />, feature: "Minimum Down Payment", details: "10% to 30% of the on-road price" },
    { icon: <FaBalanceScale className="text-3xl text-blue-700 mb-4" />, feature: "Loan to Value (LTV)", details: "Up to 90% for select profiles" },
    { icon: <FaCheckCircle className="text-3xl text-blue-700 mb-4" />, feature: "Approval Timeline", details: "Within 48–96 hours after document submission" },
    { icon: <FaExchangeAlt className="text-3xl text-blue-700 mb-4" />, feature: "EMI Options", details: "Standard, Step-Up, Balloon, EMI Holiday" },
  ];

  return (
    <>
      <Helmet>
        <title>Mercedes-Benz Car Loan in India – Compare Rates & Apply Online</title>
        <meta
          name="description"
          content="Explore Mercedes-Benz car loan options in India. Compare interest rates, EMI, and apply online for models like A-Class, C-Class, GLA, and more."
        />
        <meta
          name="keywords"
          content="mercedes-benz car loan, mercedes c-class loan, mercedes gla loan, car loan emi calculator"
        />
            <link rel="canonical" href="https://thefinmaster.com/mercedes-car-loan" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/mercedes-benz-car-loan" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Mercedes-Benz Car Loan in India" />
        <meta property="og:description" content="Compare and apply for Mercedes-Benz car loans online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/mercedes-benz-car-loan" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Mercedes-Benz Car Loan</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Mercedes-Benz Car Loan </span>  
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Mercedes-Benz is synonymous with luxury, performance, and advanced engineering. Whether you are planning to buy a sleek C-Class or a commanding GLE, the right car loan can make owning your dream car more achievable. This guide walks you through everything you need to know about getting a car loan for a Mercedes-Benz, from interest rates and eligibility to documentation, repayment tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/mercedescarloan.jpeg"
                alt="Mercedes-Benz Car"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            Popular Mercedes-Benz Cars Eligible for Car Loans
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Mercedes-Benz models are widely financed by top Indian banks and luxury lending partners. Both new and certified pre-owned Mercedes-Benz vehicles are eligible for car loans with flexible repayment options.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="w-full bg-gradient-to-b from-white via-blue-50 to-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center mb-4">
                         Mercedes-Benz Car Loan Highlights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-2 md:px-4">
            {highlights.map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden p-6 rounded-2xl shadow-xl bg-white/70 backdrop-blur-xl border border-gray-200 transition-transform transform hover:scale-105"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-blue-300 via-pink-300 to-yellow-300 rounded-full opacity-30 blur-3xl group-hover:opacity-50 transition-all"></div>
                <div className="text-3xl text-blue-700 mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.feature}</h3>
                <p className="text-gray-700">{item.details}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Banks Offering Mercedes-Benz Car Loans
        </h2>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { lender: "HDFC Bank", rate: "8.50%", tenure: "7 years", features: "Instant approval, luxury loan desk" },
              { lender: "Axis Bank", rate: "9.00%", tenure: "5 years", features: "High loan amounts for luxury segment" },
              { lender: "ICICI Bank", rate: "8.75%", tenure: "7 years", features: "Pre-approved offers for select customers" },
              { lender: "Kotak Mahindra", rate: "9.25%", tenure: "6 years", features: "Customized luxury car loans" },
              { lender: "IndusInd Bank", rate: "9.50%", tenure: "7 years", features: "Tailored loans for professionals and entrepreneurs" },
              { lender: "YES BANK", rate: "9.75%", tenure: "6 years", features: "Speedy processing, doorstep documentation" },
              { lender: "Mercedes-Benz Financial Services", rate: "Custom", tenure: "7 years", features: "In-house financing with tailored EMI plans" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.lender}</h3>
                <div className="text-gray-600 mb-1"><strong>Starting Interest Rate:</strong> {item.rate}</div>
                <div className="text-gray-600 mb-1"><strong>Max Tenure:</strong> {item.tenure}</div>
                <div className="text-gray-600"><strong>Key Features:</strong> {item.features}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Lender</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Interest Rate</th>
                  <th className="p-3 border border-gray-300 text-left">Max Tenure</th>
                  <th className="p-3 border border-gray-300 text-left">Key Features</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["HDFC Bank", "8.50%", "7 years", "Instant approval, luxury loan desk"],
                  ["Axis Bank", "9.00%", "5 years", "High loan amounts for luxury segment"],
                  ["ICICI Bank", "8.75%", "7 years", "Pre-approved offers for select customers"],
                  ["Kotak Mahindra", "9.25%", "6 years", "Customized luxury car loans"],
                  ["IndusInd Bank", "9.50%", "7 years", "Tailored loans for professionals and entrepreneurs"],
                  ["YES BANK", "9.75%", "6 years", "Speedy processing, doorstep documentation"],
                  ["Mercedes-Benz Financial Services", "Custom", "7 years", "In-house financing with tailored EMI plans"],
                ].map(([lender, rate, tenure, features], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{lender}</td>
                    <td className="p-3 border border-gray-300">{rate}</td>
                    <td className="p-3 border border-gray-300">{tenure}</td>
                    <td className="p-3 border border-gray-300">{features}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Loan EMI <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your EMI — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-loan-emi-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Sample EMI Calculation for Mercedes-Benz Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          You can customize your EMI using TheFinMaster’s EMI Calculator by adjusting the tenure, down payment, and interest rate.
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "Mercedes-Benz A-Class Limousine", price: "₹50 Lakh", loan: "₹45 Lakh", emi: "₹92,400" },
              { model: "Mercedes-Benz C-Class", price: "₹65 Lakh", loan: "₹58.5 Lakh", emi: "₹1,20,000" },
              { model: "Mercedes-Benz GLA", price: "₹55 Lakh", loan: "₹49.5 Lakh", emi: "₹1,01,500" },
              { model: "Mercedes-Benz E-Class", price: "₹90 Lakh", loan: "₹81 Lakh", emi: "₹1,66,000" },
              { model: "Mercedes-Benz GLS", price: "₹1.35 Crore", loan: "₹1.2 Crore", emi: "₹2,46,000" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>On-Road Price:</strong> {item.price}</div>
                <div className="text-gray-600 mb-1"><strong>Loan Amount:</strong> {item.loan}</div>
                <div className="text-gray-600"><strong>EMI (5 Years @10%):</strong> {item.emi}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Model</th>
                  <th className="p-3 border border-gray-300 text-left">On-Road Price (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Loan Amount (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">EMI (5 Years @10%)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Mercedes-Benz A-Class Limousine", "₹50 Lakh", "₹45 Lakh", "₹92,400"],
                  ["Mercedes-Benz C-Class", "₹65 Lakh", "₹58.5 Lakh", "₹1,20,000"],
                  ["Mercedes-Benz GLA", "₹55 Lakh", "₹49.5 Lakh", "₹1,01,500"],
                  ["Mercedes-Benz E-Class", "₹90 Lakh", "₹81 Lakh", "₹1,66,000"],
                  ["Mercedes-Benz GLS", "₹1.35 Crore", "₹1.2 Crore", "₹2,46,000"],
                ].map(([model, price, loan, emi], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{price}</td>
                    <td className="p-3 border border-gray-300">{loan}</td>
                    <td className="p-3 border border-gray-300">{emi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Mercedes-Benz Car Loan
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For Salaried Applicants:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300 mb-6">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN Card and Aadhaar Card</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Last 3–6 months' salary slips</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Form 16 or ITR of the last 2 years</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Bank statements for the last 6 months</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Address proof (utility bill, rent agreement, etc.)</span>
              </div>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For Self-Employed Applicants:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN Card and Aadhaar Card</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>GST certificate or business registration</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>ITRs for the past 2–3 years</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Audited financial statements</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Business address proof</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Bank statements (last 6 months)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <BiCar />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Owning a Mercedes-Benz is not just about driving a car — it’s about experiencing a global legacy of performance and prestige. Whether you’re buying your first luxury car or upgrading to a newer model, a tailored car loan makes the journey smoother.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Use TheFinMaster to explore competitive financing options, compare banks, and apply online with ease. Drive home your Mercedes-Benz with confidence and financial clarity.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default MercedesBenzCarLoan;